import { useState, useEffect, memo } from 'react';
import { getIcon } from '../utils/icons';
import { useUser } from '../utils/hooks';
import { useAuth } from '@/components/auth/AuthProvider';

interface UserAvatarProps {
  size?: string;
}

const UserAvatar = memo(({ size = "w-8 h-8 sm:w-9 sm:h-9" }: UserAvatarProps) => {
  const { user, loading: isLoading, reload } = useUser();
  const { user: authUser, loading: authLoading } = useAuth();
  const [imageError, setImageError] = useState(false);
  const [imageKey, setImageKey] = useState(Date.now());
  
  const UserIcon = getIcon('User');



  // Verificar se o avatar_url é uma URL válida do Supabase Storage
  const isValidAvatarUrl = user?.avatar_url && (
    user.avatar_url.includes('supabase') || 
    user.avatar_url.startsWith('http')
  );
  
  const avatarUrl = isValidAvatarUrl ? user.avatar_url : null;

  // Escutar evento de atualização do avatar
  useEffect(() => {
    const handleAvatarUpdate = () => {
      setImageError(false);
      setImageKey(Date.now()); // Forçar recarregamento da imagem
      // Recarregar dados do usuário sem recarregar a página
      reload();
    };

    window.addEventListener('userAvatarUpdated', handleAvatarUpdate);
    
    return () => { 
      window.removeEventListener('userAvatarUpdated', handleAvatarUpdate);
    };
  }, [reload]);

  // Função para gerar iniciais do nome
  const getInitials = () => {
    if (!user) return 'U';
    
    const displayName = user.full_name || user.auth_user?.email;
    if (!displayName) return 'U';
    
    return displayName
      .split(' ')
      .map((name: string) => name.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  };

  // Função para gerar cor de fundo baseada no nome
  const getBackgroundColor = () => {
    if (!user) return 'bg-gray-400';
    
    const displayName = user.full_name || user.auth_user?.email;
    if (!displayName) return 'bg-gray-400';
    
    // Gerar cor baseada no hash do nome
    const hash = displayName.split('').reduce((acc: number, char: string) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500',
      'bg-indigo-500', 'bg-yellow-500', 'bg-red-500', 'bg-teal-500'
    ];
    
    return colors[Math.abs(hash) % colors.length];
  };

  // Handler para erro de imagem
  const handleImageError = () => {
    setImageError(true);
  };

  // Loading state - só mostrar loading se não temos dados do usuário
  if ((isLoading || authLoading) && !user && !authUser) {
    return (
      <div className={`${size} bg-white bg-opacity-20 rounded-full animate-pulse flex-shrink-0`} />
    );
  }
  
  // Se não temos usuário autenticado, não mostrar avatar
  if (!authUser) {
    return (
      <div className={`${size} bg-gray-400 rounded-full flex items-center justify-center flex-shrink-0 text-white font-semibold text-sm`}>
        <UserIcon className="w-4 h-4 sm:w-5 sm:h-5" />
      </div>
    );
  }

  // Estado sem foto ou com erro na imagem
  if (!avatarUrl || imageError) {
    return (
      <div className={`${size} ${getBackgroundColor()} rounded-full flex items-center justify-center flex-shrink-0 text-white font-semibold text-sm`}>
        {user ? (
          <span className="text-xs font-bold">
            {getInitials()}
          </span>
        ) : (
          <UserIcon className="w-4 h-4 sm:w-5 sm:h-5" />
        )}
      </div>
    );
  }

  // Estado com foto do usuário
  return (
    <div className={`${size} rounded-full overflow-hidden flex-shrink-0 bg-white bg-opacity-20`}>
      <img
        src={`${avatarUrl}?t=${imageKey}`}
        alt={`Foto de ${user?.full_name || 'usuário'}`}
        className="w-full h-full object-cover"
        onError={handleImageError}
        // CRÍTICO: Não usar loading="lazy" no header pois é above-the-fold
        // loading="eager" garante carregamento imediato da foto crítica
        loading="eager"
        // Otimizações de performance
        decoding="async"
        // Tamanhos otimizados para diferentes densidades de pixel
        sizes="(max-width: 640px) 32px, 36px"
      />
    </div>
  );
});

UserAvatar.displayName = 'UserAvatar';

export default UserAvatar;
